import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Bell,
  ChevronDown,
  CreditCard,
  Heart,
  LogOut,
  MapPin,
  Package,
  Settings,
  Truck,
  User,
} from 'lucide-react';
import { useAuth, useSignOut } from '@kss/auth';

/**
 * Storefront-side account dropdown for the signed-in customer.
 *
 * Replaces the previous "single-link avatar" in `WebHeader` so a
 * customer can jump to Orders / Track order / Profile / Settings /
 * Addresses / Wishlist / Notifications / Sign out from any page —
 * matching the affordance Amazon / Flipkart users expect.
 *
 * Bug ref: AI Agent 2026-05-14 — "login button dropdown should show
 * order, profile, setting, track order other subject".
 */

const ITEMS: ReadonlyArray<{
  to: string;
  label: string;
  icon: typeof User;
  hint?: string;
}> = [
  { to: '/account', label: 'My account', icon: User, hint: 'Dashboard overview' },
  { to: '/account/orders', label: 'My orders', icon: Package, hint: 'View, cancel, reorder' },
  { to: '/account/track-order', label: 'Track order', icon: Truck },
  { to: '/account/profile', label: 'Profile', icon: User },
  { to: '/account/addresses', label: 'Saved addresses', icon: MapPin },
  { to: '/account/payment-methods', label: 'Payment methods', icon: CreditCard },
  { to: '/account/wishlist', label: 'Wishlist', icon: Heart },
  { to: '/account/notifications', label: 'Notifications', icon: Bell },
  { to: '/account/profile#security', label: 'Settings', icon: Settings, hint: 'Password, preferences' },
];

export function CustomerAccountDropdown() {
  const { user } = useAuth();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Outside-click + Escape closes
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const displayName = user?.name?.trim() || user?.email || 'Account';
  const email = user?.email ?? '';
  const initial = (displayName.charAt(0) || 'K').toUpperCase();
  const avatar = user?.avatar;

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
    } finally {
      setSigningOut(false);
      setOpen(false);
      navigate('/', { replace: true });
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        className="inline-flex h-9 items-center gap-1 rounded-full bg-primary px-1.5 text-primary-foreground hover:bg-primary-hover"
      >
        {avatar ? (
          <img
            src={avatar}
            alt=""
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
            className="h-7 w-7 rounded-full object-cover"
          />
        ) : (
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/15 text-xs font-bold">
            {initial}
          </span>
        )}
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-2 w-64 overflow-hidden rounded-lg border border-border bg-surface-card shadow-lg"
        >
          {/* Header — real user info */}
          <div className="flex items-center gap-3 border-b border-border bg-surface-card-hover px-4 py-3">
            {avatar ? (
              <img src={avatar} alt="" className="h-10 w-10 rounded-full object-cover" />
            ) : (
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                {initial}
              </span>
            )}
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-content-primary">
                {displayName}
              </p>
              {email && (
                <p className="truncate text-xs text-content-muted">{email}</p>
              )}
            </div>
          </div>

          {/* Items */}
          <nav className="py-1">
            {ITEMS.map(({ to, label, icon: Icon, hint }) => (
              <Link
                key={to}
                to={to}
                role="menuitem"
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 px-4 py-2 text-sm text-content-secondary transition-colors hover:bg-surface-card-hover hover:text-content-primary"
              >
                <Icon className="h-4 w-4 text-content-muted" />
                <span className="flex-1">{label}</span>
                {hint && (
                  <span className="text-[10px] text-content-muted">{hint}</span>
                )}
              </Link>
            ))}
          </nav>

          {/* Sign out */}
          <div className="border-t border-border py-1">
            <button
              type="button"
              role="menuitem"
              onClick={handleSignOut}
              disabled={signingOut}
              className="flex w-full items-center gap-3 px-4 py-2 text-sm text-danger transition-colors hover:bg-danger-light disabled:opacity-60"
            >
              <LogOut className="h-4 w-4" />
              {signingOut ? 'Signing out…' : 'Sign out'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomerAccountDropdown;
